import { callProvider } from '@/features/dictation/transports';
import { monitorSpeech } from './monitor-speech';

/** The longest a provider says at once (`inference.v1` `speech`). */
const MAX_SPOKEN = 5_000;

type Spoken = { audio: string; mimeType: string };

/**
 * Says words in a voice of the user's speech provider (a voice's sample in Settings › Voice):
 * resolves once they were heard, or at once when `signal` stops them. A voice the provider does
 * not have falls back to its default one. `onLevel` follows how loud it is.
 */
export async function speakAs(
  text: string,
  voice: string | undefined,
  options: {
    signal: AbortSignal;
    onStart(): void;
    onLevel(level: number): void;
  },
): Promise<void> {
  const said = text.slice(0, MAX_SPOKEN);
  const spoken = await callProvider<Spoken>('inference.v1', 'speech', {
    text: said,
    ...(voice === undefined ? {} : { voice }),
  }).catch((error: unknown) => {
    if (voice === undefined) throw error;
    return callProvider<Spoken>('inference.v1', 'speech', { text: said });
  });
  if (options.signal.aborted) return;
  const audio = new Audio(`data:${spoken.mimeType};base64,${spoken.audio}`);
  await new Promise<void>((resolve, reject) => {
    let monitor: { close(): void } | null = null;
    const finish = () => {
      monitor?.close();
      options.onLevel(0);
      options.signal.removeEventListener('abort', stop);
      resolve();
    };
    const stop = () => {
      audio.pause();
      finish();
    };
    options.signal.addEventListener('abort', stop, { once: true });
    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', () => reject(new Error('The voice could not be played.')), {
      once: true,
    });
    audio.play().then(
      () => {
        options.onStart();
        // Its level, where the browser hands the element's sound over (Chromium, Electron).
        const capture = (audio as HTMLAudioElement & { captureStream?: () => MediaStream })
          .captureStream;
        if (capture !== undefined)
          monitor = monitorSpeech(capture.call(audio), () => undefined, options.onLevel);
      },
      (error: unknown) => reject(error instanceof Error ? error : new Error(String(error))),
    );
  });
}
